import { collection, doc, getDoc, getDocs, limit, query, where } from 'firebase/firestore';
import { firestore } from '../firebase';
import { getEventByYear, getFeaturedYear, parseEditionYear } from './eventRouting';

/**
 * Two axes (keep them separate in UI and code):
 *
 * - Admin working event: `getActiveEventId()` → `_config/system.activeEventId` (plus legacy `isActive`).
 *   Use for protected admin tools and defaults when no `?eventId` is present.
 *
 * - Public canonical event: `getPublicDefaultEventId()` → featured year / auto-live rules in
 *   `_config/publicSite` + `events.*.liveMode` (legacy: `publicLifecyclePhase === 'live'`), then fallback to active event id.
 *   Use for public routes like /schedule, /rules, and for stragglers that must match printed links.
 */

/**
 * Active event selection (migration target).
 *
 * Source of truth: `_config/system.activeEventId`
 * Fallbacks (for legacy installs / while migrating):
 * - First event with `isActive == true`
 */

export async function getActiveEventId() {
  try {
    const sys = await getDoc(doc(firestore, '_config', 'system'));
    const id = sys.exists() ? sys.data()?.activeEventId : null;
    const s = (id || '').toString().trim();
    if (s) return s;
  } catch {
    // ignore
  }

  try {
    const snap = await getDocs(
      query(collection(firestore, 'events'), where('isActive', '==', true), limit(1))
    );
    if (!snap.empty) return snap.docs[0].id;
  } catch {
    // ignore
  }

  return '';
}

export async function getActiveEvent() {
  const id = await getActiveEventId();
  if (!id) return null;
  const snap = await getDoc(doc(firestore, 'events', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Public default event selection for legacy public routes (/schedule, /rules, /map, /leaderboard, etc).
 * Matches the same year selection logic used by root "/" routing:
 * - auto live year (when rootMode allows)
 * - otherwise featured year
 * - then active defaults fallback
 */
export async function getPublicDefaultEventId() {
  try {
    const year = await getFeaturedYear();
    if (year) {
      const event = await getEventByYear(year);
      if (event?.id) return event.id;
    }
  } catch {
    // ignore and fall back to active defaults
  }
  return getActiveEventId();
}

/**
 * Public pages: `?event=` wins, then URL edition year (e.g. /2026/schedule), then public default.
 */
export async function resolvePublicPageEventId({ eventIdParam, editionYearParam }) {
  const eid = (eventIdParam || '').toString().trim();
  if (eid) return eid;
  const y = parseEditionYear(editionYearParam);
  if (y) {
    const ev = await getEventByYear(y);
    if (ev?.id) return ev.id;
  }
  return getPublicDefaultEventId();
}

/**
 * `/volunteer`: `?event=` wins, then `_config/system.volunteerEventId`, then public default.
 */
export async function resolveVolunteerPageEventId(eventIdParam) {
  const eid = (eventIdParam || '').toString().trim();
  if (eid) return eid;
  try {
    const sys = await getDoc(doc(firestore, '_config', 'system'));
    const volunteerId = sys.exists() ? (sys.data()?.volunteerEventId || '') : '';
    const s = (volunteerId || '').toString().trim();
    if (s) return s;
  } catch {
    // ignore and use fallback
  }
  return getPublicDefaultEventId();
}

/**
 * Resolves the event for /dashboard: ?event= wins, then ?year= (edition), then public default.
 * @param {{ eventIdParam?: string|null, editionYearParam?: string|null }} args
 * @returns {Promise<string>}
 */
export async function resolveDashboardEventId({ eventIdParam, editionYearParam }) {
  const eid = (eventIdParam || '').toString().trim();
  if (eid) return eid;
  const y = parseEditionYear(editionYearParam);
  if (y) {
    const ev = await getEventByYear(y);
    if (ev?.id) return ev.id;
  }
  return getPublicDefaultEventId();
}

export async function getEventById(eventId) {
  const id = (eventId || '').toString().trim();
  if (!id) return null;
  const snap = await getDoc(doc(firestore, 'events', id));
  return snap.exists() ? { id: snap.id, ...snap.data() } : null;
}

/**
 * Default selection for admin event dropdowns.
 * Order: explicit `?event` / `eventId` when present and valid → configured active event → first in list.
 */
export function resolveAdminDefaultEventId({ eventIdParam, activeEventId, events }) {
  const list = Array.isArray(events) ? events : [];
  const idSet = new Set(
    list
      .map((e) => (e && e.id != null ? String(e.id).trim() : ''))
      .filter(Boolean)
  );
  const param = (eventIdParam || '').toString().trim();
  if (param && idSet.has(param)) return param;
  const active = (activeEventId || '').toString().trim();
  if (active && idSet.has(active)) return active;
  return list[0]?.id || '';
}

